<template>
  <div id="project-details-create" class="professional-attachment-dialog">
    <!-- Professional Overlay Loading -->
    <v-overlay :value="OverlayLoadingFlag" class="professional-overlay" z-index="9999">
      <div class="loading-container">
        <v-progress-circular
          indeterminate
          size="64"
          width="4"
          color="#1976d2"
        ></v-progress-circular>
        <p class="loading-text mt-4">Loading event details...</p>
      </div>
    </v-overlay>

    <!-- Professional Main Card -->
    <v-card class="dialog-card" elevation="8">
      <!-- Professional Header -->
      <v-card-title class="dialog-header pa-5">
        <div class="header-content">
          <div class="title-section">
            <v-icon size="28" color="white" class="mr-3">mdi-calendar-star</v-icon>
            <h2 class="dialog-title">{{ pageTitle }}</h2>
          </div>
          <v-btn icon dark @click="closePrompt" large>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Professional Content -->
      <v-card-text class="dialog-content pa-6">
        <!-- Loading Section -->
        <div class="loading-section" v-if="LoadingFlag">
          <v-progress-circular
            indeterminate
            color="#1976d2"
            size="48"
            width="4"
          ></v-progress-circular>
          <p class="loading-text mt-3">Loading...</p>
        </div>

        <!-- Form Section -->
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="confirmAlert"
          class="professional-form"
        >
          <!-- Selection Section -->
          <v-card class="selection-card mb-5" elevation="2" outlined>
            <v-card-title class="selection-header pa-4">
              <v-icon size="20" color="#1976d2" class="mr-2">mdi-filter</v-icon>
              <span class="selection-title">Event Selection</span>
            </v-card-title>
            <v-card-text class="pa-5">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <h4 class="input-label">
                      <span class="required-star">*</span> Month Name
                    </h4>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :items="MonthOptions"
                      :rules="MonthRules"
                      v-model="Month"
                      :loading="MonthOptionsLoading"
                      dense
                      clearable
                      required
                      outlined
                      placeholder="Select month..."
                      prepend-inner-icon="mdi-calendar-month"
                    ></v-autocomplete>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <h4 class="input-label">
                      <span class="required-star">*</span> Event Name
                    </h4>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :items="EventOptions"
                      :rules="EventRules"
                      :loading="EventOptionsLoading"
                      v-model="Event"
                      dense
                      clearable
                      required
                      outlined
                      placeholder="Select event..."
                      prepend-inner-icon="mdi-calendar-star"
                    ></v-autocomplete>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Event Details Section -->
          <v-card v-if="eventData != ''" class="event-details-card mb-5" elevation="3">
            <v-card-title class="event-details-header pa-4">
              <v-icon size="22" color="#1976d2" class="mr-2">mdi-information</v-icon>
              <span class="event-details-title">Event Details</span>
            </v-card-title>
            <v-card-text class="pa-5">
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <div class="detail-box">
                    <label class="detail-label">
                      <v-icon size="16" color="#666" class="mr-1">mdi-calendar</v-icon>
                      Event Date
                    </label>
                    <p class="detail-value">{{ eventData.DateFromTxt }}</p>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="detail-box">
                    <label class="detail-label">
                      <v-icon size="16" color="#666" class="mr-1">mdi-briefcase</v-icon>
                      Portfolio Type
                    </label>
                    <p class="detail-value">{{ eventData.PortfolioName }}</p>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="detail-box">
                    <label class="detail-label">
                      <v-icon size="16" color="#666" class="mr-1">mdi-calendar-star</v-icon>
                      Event Name
                    </label>
                    <p class="detail-value">{{ eventData.JciEventName }}</p>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="detail-box">
                    <label class="detail-label">
                      <v-icon size="16" color="#666" class="mr-1">mdi-image-multiple</v-icon>
                      Event Images
                    </label>
                    <div class="event-images mt-2">
                      <viewer rebuild :images="eventData.WebsiteImages">
                        <div class="images-grid">
                          <img
                            class="event-image"
                            width="80"
                            height="80"
                            :src="src"
                            :key="src"
                            v-for="src in eventData.WebsiteImages"
                          />
                        </div>
                      </viewer>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="8">
                  <div class="detail-box">
                    <label class="detail-label">
                      <v-icon size="16" color="#666" class="mr-1">mdi-text</v-icon>
                      Event Description
                    </label>
                    <p class="detail-value description-text">{{ eventData.JciEventDescription }}</p>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Action Buttons -->
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                :disabled="eventData == ''"
                @click.prevent="addEvent"
                :loading="SubmitFlag"
                elevation="8"
                rounded
                large
                color="#1976d2"
                class="action-btn white--text mr-3"
              >
                <v-icon left>mdi-plus-circle</v-icon>
                Add Event
              </v-btn>
              <v-btn
                @click="closePrompt"
                elevation="8"
                rounded
                large
                color="#757575"
                class="action-btn white--text"
              >
                <v-icon left>mdi-close-circle</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

    <script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    addRecordPrompt: {
      required: true,
      type: Boolean,
    },
    pageTitle: {
      required: true,
    },
    recordData: {
      required: true,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      OverlayLoadingFlag: false,
      SubmitFlag: false,
      SearchFlag: false,

      UploadFlag: false,
      ResultFlag: false,

      addButtonFlag: true,

      eventData: "",
      Project: {},

      MonthRules: [(v) => !!v || "Month Name is required"],
      Month: "",
      MonthOptions: [],
      MonthOptionsLoading: false,

      PortfolioRules: [(v) => !!v || "Portfolio is required"],
      Portfolio: "",
      PortfolioOptions: [],
      PortfolioOptionsLoading: false,

      EventRules: [(v) => !!v || "Event Name is required"],
      Event: "",
      EventOptions: [],
      EventOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;
      }
    },
    MonthOptions: function () {
      console.log("watch MonthOptions");
      this.MonthOptionsLoading = false;
    },
    PortfolioOptions: function () {
      console.log("watch PortfolioOptions");
      this.PortfolioOptionsLoading = false;
    },
    EventOptions: function () {
      console.log("watch EventOptions");
      this.EventOptionsLoading = false;
    },
    Month: function () {
      console.log("Portfolio called");
      this.Portfolio = "";
      this.Event = "";
      this.eventData = "";
      // this.getPortfolioOptions();
      this.getEventOptions();
    },
    // Portfolio: function () {
    //   console.log("Portfolio called");
    //   this.Event = "";
    //   this.eventData = "";
    //   this.getEventOptions();
    // },
    Event: function () {
      console.log("Event called");
      this.eventData = "";
      this.searchForm();
    },
  },
  mounted() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      this.LoadingFlag = false;

      this.getMonthOptions();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.Project = {
        EventId: 0,
      };
      this.$emit("hideAddAttachmentPrompt", this.Project);
    },
    getMonthOptions() {
      console.log("getMonthOptions called");
      this.MonthOptionsLoading = true;
      var selectbox1_source = "Month";
      var selectbox1_destination = "MonthOptions";
      var selectbox1_url = "api/jci-month/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getPortfolioOptions() {
      console.log("getPortfolioOptions called");
      this.PortfolioOptionsLoading = true;
      var selectbox1_source = "Portfolio";
      var selectbox1_destination = "PortfolioOptions";
      var selectbox1_url = "api/portfolio/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getEventOptions() {
      console.log("getEventOptions called");
      this.EventOptionsLoading = true;
      var selectbox1_source = "Event";
      var selectbox1_destination = "EventOptions";
      var selectbox1_url = "api/jci-events/options";

      var Month = this.Month;
      console.log({ Month });

      if (Month > 0) {
        var selectbox1_conditions_array = {
          UserInterface: 2,
          Month: Month,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    searchForm() {
      console.log("searchForm called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        this.tableData1 = [];
        this.selected1 = [];
        this.SearchFlag = true;
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/show";
        var upload = {
          UserInterface: 1,
          EventCode: this.Event,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var table_content = [];
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SearchFlag = false;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.eventData = records;

              thisIns.toast("success", output);
            } else {
              console.log("error=" + output);
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "Kindy fill required fields and then submit";
        this.toast("error", message);
      }
    },
    addEvent() {
      console.log("addEvent called");

      var eventData = this.eventData;
      console.log({ eventData });

      if (eventData != "") {
        this.Project = {
          EventId: eventData.JciEventId,
          EventName: eventData.JciEventName,
          EventDate: eventData.DateFrom1,
          PortfolioId: eventData.PortfolioId,
          PortfolioName: eventData.PortfolioName,
          EventDescription: eventData.JciEventDescription,
          EventPhoto: eventData.WebsiteImages,
        };
        this.$emit("hideAddAttachmentPrompt", this.Project);
        this.eventData = "";
        this.Project = {};
      }
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        // this.toast("error", "Kindly fill the required fields", true);
      }
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Professional Attachment Dialog Styles
.professional-attachment-dialog {
  // Professional Overlay
  .professional-overlay {
    z-index: 9999 !important;

    .loading-container {
      text-align: center;
      color: white;

      .loading-text {
        font-size: 1.1rem;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  .dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
    background: white;
  }

  // Professional Header
  .dialog-header {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: white;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      .title-section {
        display: flex;
        align-items: center;

        .dialog-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  // Content Section
  .dialog-content {
    background: #f5f7fa;

    .loading-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem 0;

      .loading-text {
        font-size: 1rem;
        color: #666;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  // Selection Card
  .selection-card {
    border-radius: 8px !important;
    border: 1px solid #e0e0e0 !important;

    .selection-header {
      background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
      border-bottom: 1px solid #e0e0e0;

      .selection-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #2c3e50;
      }
    }

    .input-wrapper {
      .input-label {
        font-size: 0.95rem;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 0.5rem;

        .required-star {
          color: #f44336;
          margin-right: 0.25rem;
        }
      }
    }
  }

  // Event Details Card
  .event-details-card {
    border-radius: 8px !important;
    border: 1px solid #e3f2fd !important;

    .event-details-header {
      background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
      border-bottom: 1px solid #e0e0e0;

      .event-details-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #2c3e50;
      }
    }

    .detail-box {
      margin-bottom: 1rem;

      .detail-label {
        font-size: 0.8rem;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
      }

      .detail-value {
        font-size: 1rem;
        color: #2c3e50;
        font-weight: 500;
        margin: 0;
        line-height: 1.4;

        &.description-text {
          line-height: 1.6;
        }
      }
    }

    .event-images {
      .images-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;

        .event-image {
          border-radius: 6px;
          border: 2px solid #e0e0e0;
          object-fit: cover;
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: pointer;

          &:hover {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
        }
      }
    }
  }

  // Action Buttons
  .action-btn {
    min-width: 140px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0.5px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15) !important;
    }
  }

  // Responsive Design
  @media (max-width: 768px) {
    .dialog-header {
      padding: 1rem !important;

      .header-content .title-section .dialog-title {
        font-size: 1.2rem;
      }
    }

    .dialog-content {
      padding: 1rem !important;
    }

    .selection-header,
    .event-details-header {
      padding: 1rem !important;

      .selection-title,
      .event-details-title {
        font-size: 1rem !important;
      }
    }

    .action-btn {
      width: 100%;
      margin: 0.25rem 0 !important;
    }
  }
}
</style>